/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect } from 'react';

import PropTypes from 'prop-types';

import { AppUtils } from '../../../core/AppUtils';
import { CursorManager } from '../../../core/navigation';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { disableGhostMode } from '../../../store/guide/guidehighlight/guidehighlight.reducers';
import { Highlight } from '../../Highlight';

interface GuideHighlightProps {
    id?: string;
    cellType?: string;
}

const GuideHighlight: FunctionComponent<GuideHighlightProps> = ({ id, cellType }): JSX.Element => {
    const { verticalPosition, horizontalPosition, ghostMode } = useTivoSelector(
        (state: RootState) => state.guideHighlight,
    );

    const { isTurboScrollActive, isGuidePageScrollActive } = useTivoSelector(
        (state: RootState) => state.turboScroll,
    );

    const { isOverlayDialogActive: isAnyGuideOverlayVisible } = useTivoSelector(
        (state: RootState) => state.overlayDialog,
    );

    const { isPastGuideMode } = useTivoSelector((state: RootState) => state.gridGuide);
    const dispatch = useTivoDispatch();

    useEffect(() => {
        if (!CursorManager.isCursorAppeared() && ghostMode) {
            dispatch(disableGhostMode());
        }
    }, [CursorManager.isCursorAppeared()]);

    const { isSpinnerActive } = useTivoSelector((state: RootState) => state.spinner);

    const currentId = AppUtils.createHighlightId(horizontalPosition, verticalPosition);
    const isActive = id === currentId;
    const isGhostHighlightActive = ghostMode && isActive;
    const isTurboModeActive = isTurboScrollActive || isGuidePageScrollActive;
    const isHighlightVisible = !(isAnyGuideOverlayVisible || isSpinnerActive || isTurboModeActive);

    return (
        <Highlight
            cellType={cellType}
            isActive={isActive}
            isGhostHighlightActive={isGhostHighlightActive}
            isHighlightVisible={isHighlightVisible || ghostMode}
            isPastGuideMode={isPastGuideMode}
        />
    );
};

GuideHighlight.propTypes = {
    id: PropTypes.string,
    cellType: PropTypes.string,
};

export default GuideHighlight;
